import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserAuth } from '../context/UserAuthContext';
import config from '../config';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Paper,
  AppBar,
  Toolbar,
  IconButton,
  Alert,
  CircularProgress,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import WarningIcon from '@mui/icons-material/Warning';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import BookIcon from '@mui/icons-material/Book';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import { getExpiryBannerDaysRemaining } from '../utils/subscriptionExpiry';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import QuizIcon from '@mui/icons-material/Quiz';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActionArea from '@mui/material/CardActionArea';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import ConstructionIcon from '@mui/icons-material/Construction';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import comingSoonMessages from '../data/comingSoonMessages.json';
import { trackDashboardTileClick, trackGAEvent, GA_EVENTS } from '../utils/googleAnalytics';
import { trackMemberActivity } from '../utils/memberActivity';

// Navigation tiles configuration
const DASHBOARD_TILES = [
  {
    id: 'magazines',
    title: 'Magazines',
    icon: MenuBookIcon,
    image: 'https://dp9h3awfnhccl.cloudfront.net/public_folder/digital-friendly.png',
    route: '/magazine',
    gradient: 'linear-gradient(135deg, #FF6B35 0%, #FFB74D 100%)',
    description: 'Read your weekly magazine',
    comingSoon: false
  },
  {
    id: 'quizzes',
    title: 'Quizzes',
    icon: QuizIcon,
    image: 'https://dp9h3awfnhccl.cloudfront.net/public_folder/make-it-easy.png',
    route: '/quizzes',
    gradient: 'linear-gradient(135deg, #06AED5 0%, #4FC3F7 100%)',
    description: 'Test your knowledge',
    comingSoon: false
  },
  {
    id: 'stories',
    title: 'Stories',
    icon: AutoStoriesIcon,
    image: 'https://dp9h3awfnhccl.cloudfront.net/public_folder/stories-banner.jpeg',
    route: '/stories',
    externalUrl: 'https://docsend.com/v/m6rbr/let-us-know-latest-topic',
    gradient: 'linear-gradient(135deg, #0D7377 0%, #14FFEC 100%)',
    description: 'Dive into meaningful stories',
    comingSoon: false
  },
  {
    id: 'fun',
    title: 'Bonus Content',
    icon: EmojiEmotionsIcon,
    image: 'https://dp9h3awfnhccl.cloudfront.net/public_folder/fun-banner.jpeg',
    route: '/fun',
    externalUrl: 'https://docsend.com/v/m6rbr/youngpresso-bonus-content',
    gradient: 'linear-gradient(135deg, #4CAF50 0%, #81C784 100%)',
    description: 'Fun and Knowledge',
    comingSoon: false
  }
];

const getRandomComingSoonMessage = (featureId) => {
  const messages = comingSoonMessages[featureId] || [];
  if (messages.length === 0) {
    return 'This feature is under construction and launching soon!';
  }
  return messages[Math.floor(Math.random() * messages.length)];
};

const UserDashboard = () => {
  const navigate = useNavigate();
  const { userData, logout, refreshUserData } = useUserAuth();
  const [daysRemaining, setDaysRemaining] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);
  const [comingSoonDialog, setComingSoonDialog] = useState({
    open: false,
    title: '',
    message: ''
  });

  // Refresh user data on mount
  useEffect(() => {
    const refreshData = async () => {
      if (refreshUserData) {
        await refreshUserData();
      }
    };
    refreshData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setDaysRemaining(getExpiryBannerDaysRemaining(userData?.subscription));
  }, [userData]);

  const handleLogout = () => {
    logout();
    navigate('/user-login');
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleTileClick = (tile) => {
    trackDashboardTileClick(tile.id);
    if (tile.id === 'magazines') {
      trackMemberActivity('magazine_card');
    }

    if (tile.comingSoon) {
      setComingSoonDialog({
        open: true,
        title: `${tile.title} — Coming Soon`,
        message: getRandomComingSoonMessage(tile.id)
      });
      return;
    }
    if (tile.externalUrl) {
      window.open(tile.externalUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate(tile.route);
  };

  const handleComingSoonClose = () => {
    setComingSoonDialog((prev) => ({ ...prev, open: false }));
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 30%, #2a2a2a 70%, #1a1a1a 100%)'
      }}
    >
      {/* Header */}
      <AppBar
        position="static"
        sx={{
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 4px 30px rgba(0, 0, 0, 0.3)'
        }}
      >
        <Toolbar>
          <MenuBookIcon sx={{ color: '#FF6B35', mr: 2, fontSize: 32 }} />
          <Typography
            variant="h6"
            component="div"
            sx={{
              flexGrow: 1,
              fontWeight: 700,
              background: 'linear-gradient(45deg, #FF6B35 30%, #FFB74D 70%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            YoungPresso
          </Typography>
          <Button
            onClick={handleMenuOpen}
            startIcon={<AccountCircleIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />}
            sx={{
              color: 'white',
              mr: { xs: 0.5, sm: 2 },
              textTransform: 'none',
              fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
              padding: { xs: '4px 8px', sm: '6px 12px' },
              minWidth: 'auto',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: { xs: '120px', sm: 'none' },
              '&:hover': {
                background: 'rgba(255, 107, 53, 0.2)'
              }
            }}
          >
            <Box
              component="span"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              Hi {userData?.name || 'User'}!
            </Box>
          </Button>
        </Toolbar>
      </AppBar>

      {/* User Profile Dropdown Menu */}
      <Menu
        anchorEl={anchorEl}
        open={openMenu}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            background: 'rgba(26, 26, 26, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 107, 53, 0.3)',
            borderRadius: 2,
            mt: 1,
            minWidth: 300
          }
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" sx={{ color: '#FFB74D', fontWeight: 600, mb: 0.5 }}>
            User Profile
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            {userData?.name || 'User'}
          </Typography>
        </Box>
        <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />
        
        <MenuItem sx={{ py: 1.5, '&:hover': { background: 'rgba(255, 107, 53, 0.1)' } }}>
          <ListItemIcon>
            {userData?.subscription?.status?.toLowerCase() === 'cancelled' ? (
              <CancelIcon sx={{ color: '#f44336' }} />
            ) : (
              <CheckCircleIcon sx={{ color: '#4CAF50' }} />
            )}
          </ListItemIcon>
          <ListItemText
            primary={<Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>Subscription Status</Typography>}
            secondary={<Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', textTransform: 'capitalize' }}>{userData?.subscription?.status || 'N/A'}</Typography>}
          />
        </MenuItem>

        <MenuItem sx={{ py: 1.5, '&:hover': { background: 'rgba(255, 107, 53, 0.1)' } }}>
          <ListItemIcon>
            <EmailIcon sx={{ color: '#06AED5' }} />
          </ListItemIcon>
          <ListItemText
            primary={<Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>Email</Typography>}
            secondary={<Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>{userData?.email || 'N/A'}</Typography>}
          />
        </MenuItem>

        <MenuItem sx={{ py: 1.5, '&:hover': { background: 'rgba(255, 107, 53, 0.1)' } }}>
          <ListItemIcon>
            <PhoneIcon sx={{ color: '#FFB74D' }} />
          </ListItemIcon>
          <ListItemText
            primary={<Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>Phone</Typography>}
            secondary={<Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>{userData?.phone || 'N/A'}</Typography>}
          />
        </MenuItem>

        <MenuItem sx={{ py: 1.5, '&:hover': { background: 'rgba(255, 107, 53, 0.1)' } }}>
          <ListItemIcon>
            <BookIcon sx={{ color: '#FF6B35' }} />
          </ListItemIcon>
          <ListItemText
            primary={<Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>Subscribed Edition</Typography>}
            secondary={<Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', textTransform: 'capitalize' }}>{userData?.edition || 'N/A'}</Typography>}
          />
        </MenuItem>

        <MenuItem sx={{ py: 1.5, '&:hover': { background: 'rgba(255, 107, 53, 0.1)' } }}>
          <ListItemIcon>
            <SubscriptionsIcon sx={{ color: '#9C27B0' }} />
          </ListItemIcon>
          <ListItemText
            primary={<Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>Subscribed Plan</Typography>}
            secondary={<Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase' }}>{userData?.period || 'N/A'}</Typography>}
          />
        </MenuItem>

        <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', my: 1 }} />
        
        <MenuItem 
          onClick={() => {
            handleMenuClose();
            handleLogout();
          }}
          sx={{ 
            py: 1.5, 
            '&:hover': { 
              background: 'rgba(255, 107, 53, 0.2)' 
            } 
          }}
        >
          <ListItemIcon>
            <LogoutIcon sx={{ color: '#FF6B35' }} />
          </ListItemIcon>
          <ListItemText
            primary={<Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>Sign Out</Typography>}
          />
        </MenuItem>
      </Menu>

      {/* Countdown Ticker for Cancelled Subscriptions */}
      {daysRemaining != null && (
        <Box
          sx={{
            background: 'linear-gradient(135deg, rgba(255, 107, 53, 0.15) 0%, rgba(255, 107, 53, 0.05) 100%)',
            borderBottom: '3px solid #FF6B35',
            backdropFilter: 'blur(10px)',
            py: { xs: 2, sm: 2.5, md: 3 },
            px: { xs: 2, sm: 3, md: 4 }
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 1.5, sm: 2, md: 3 },
              flexDirection: { xs: 'column', sm: 'row' },
              textAlign: { xs: 'center', sm: 'left' }
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: '#FF6B35',
                  mb: 1,
                  fontSize: { xs: '1.2rem', sm: '1.5rem', md: '1.8rem' },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: { xs: 'center', sm: 'flex-start' },
                  gap: 1,
                  flexWrap: 'wrap'
                }}
              >
                <WarningIcon
                  sx={{
                    fontSize: { xs: 28, sm: 32, md: 36 },
                    animation: 'pulse 2s ease-in-out infinite',
                    '@keyframes pulse': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0.5 }
                    }
                  }}
                />
                Uh oh! ⏰ Your access expires in {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontSize: { xs: '0.85rem', sm: '0.95rem', md: '1rem' }
                }}
              >
                � Don't let your subscription lapse! Subscribe again and continue the learning journey with YoungPresso.
              </Typography>
            </Box>
            <Button
              variant="contained"
              onClick={() => navigate('/pricing')}
              sx={{
                background: 'linear-gradient(45deg, #FF6B35 30%, #FFB74D 70%)',
                color: 'white',
                fontWeight: 700,
                px: { xs: 2.5, sm: 3, md: 4 },
                py: { xs: 1, sm: 1.2, md: 1.5 },
                fontSize: { xs: '0.85rem', sm: '0.95rem', md: '1rem' },
                whiteSpace: 'nowrap',
                '&:hover': {
                  background: 'linear-gradient(45deg, #FF8C35 30%, #FFC74D 70%)',
                  transform: 'scale(1.05)',
                  boxShadow: '0 6px 20px rgba(255, 107, 53, 0.5)'
                },
                transition: 'all 0.3s ease',
                flexShrink: 0
              }}
            >
              Subscribe Again
            </Button>
          </Box>
        </Box>
      )}

      <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 4, md: 5 } }}>
        {/* Welcome Message */}
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            background: 'linear-gradient(45deg, #FF6B35 30%, #FFB74D 70%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: { xs: 1, sm: 1.5, md: 2 },
            textAlign: 'center',
            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' }
          }}
        >
          Welcome, {userData?.name || 'User'}! 👋
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: 'rgba(255, 255, 255, 0.7)',
            textAlign: 'center',
            mb: { xs: 3, sm: 4, md: 5 },
            fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' }
          }}
        >
          Choose where you'd like to go
        </Typography>

        {/* Navigation Tiles */}
        <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
          {DASHBOARD_TILES.map((tile, index) => {
            const IconComponent = tile.icon;
            return (
              <Grid item xs={6} sm={6} md={6} key={tile.id}>
                <Card
                  sx={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    backdropFilter: 'blur(10px)',
                    border: '2px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: { xs: 2, sm: 3, md: 3 },
                    transition: 'all 0.3s ease',
                    animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`,
                    '@keyframes fadeInUp': {
                      '0%': {
                        opacity: 0,
                        transform: 'translateY(30px)'
                      },
                      '100%': {
                        opacity: 1,
                        transform: 'translateY(0)'
                      }
                    },
                    '&:hover': {
                      transform: { xs: 'translateY(-4px)', sm: 'translateY(-8px)' },
                      boxShadow: '0 12px 40px rgba(255, 107, 53, 0.3)',
                      border: '2px solid rgba(255, 107, 53, 0.5)'
                    }
                  }}
                >
                  <CardActionArea onClick={() => handleTileClick(tile)}>
                    <CardContent
                      sx={{
                        p: { xs: 2, sm: 3, md: 3 },
                        textAlign: 'center'
                      }}
                    >
                      {/* Image or Icon */}
                      {tile.image ? (
                        <Box
                          component="img"
                          src={tile.image}
                          alt={tile.title}
                          sx={{
                            width: '100%',
                            aspectRatio: '1 / 1',
                            height: 'auto',
                            objectFit: 'cover',
                            borderRadius: 2,
                            mb: 2,
                            display: 'block'
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: '100%',
                            aspectRatio: '1 / 1',
                            borderRadius: 2,
                            mb: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: tile.gradient
                          }}
                        >
                          <IconComponent
                            sx={{
                              fontSize: { xs: 60, sm: 75, md: 90 },
                              color: 'white'
                            }}
                          />
                        </Box>
                      )}

                      {/* Title */}
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: 'white',
                          mb: 0.5,
                          fontSize: { xs: '1rem', sm: '1.2rem', md: '1.4rem' }
                        }}
                      >
                        {tile.title}
                      </Typography>

                      {/* Description */}
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'rgba(255, 255, 255, 0.6)',
                          fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.9rem' }
                        }}
                      >
                        {tile.description}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* Gifting CTA */}
        <Box
          sx={{
            mt: { xs: 4, sm: 5, md: 6 },
            p: { xs: 2.5, sm: 3, md: 4 },
            borderRadius: { xs: 2, sm: 3 },
            background: 'linear-gradient(135deg, rgba(255, 107, 53, 0.18) 0%, rgba(255, 183, 77, 0.08) 100%)',
            border: '2px solid rgba(255, 107, 53, 0.35)',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: { xs: 1.5, sm: 2 }
          }}
        >
          <Box
            component="img"
            src="https://dp9h3awfnhccl.cloudfront.net/public_folder/gifting-banner-home.jpeg"
            alt="Happy Birthday YoungPresso"
            sx={{
              width: { xs: '70%', sm: '50%', md: '40%' },
              maxWidth: 360,
              height: 'auto',
              borderRadius: 2,
              display: 'block'
            }}
          />
          <CardGiftcardIcon
            sx={{
              fontSize: { xs: 36, sm: 44 },
              color: '#FFB74D'
            }}
          />
          <Typography
            sx={{
              color: 'white',
              fontWeight: 700,
              fontSize: { xs: '1.05rem', sm: '1.25rem', md: '1.4rem' },
              lineHeight: 1.4,
              maxWidth: 640
            }}
          >
            Want to gift YoungPresso to your friend&apos;s kids, nephews, or little buddies?
          </Typography>
          <Typography
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: { xs: '0.85rem', sm: '0.95rem', md: '1rem' },
              maxWidth: 560
            }}
          >
            Spread the joy of reading — check out our gifting options for YoungPresso.
          </Typography>
          <Button
            variant="contained"
            startIcon={<CardGiftcardIcon />}
            onClick={() => {
              trackGAEvent(GA_EVENTS.CLICKED_ON_GIFTING);
              navigate('/gifting-secured');
            }}
            sx={{
              mt: 0.5,
              background: 'linear-gradient(45deg, #FF6B35 30%, #FFB74D 70%)',
              color: 'white',
              fontWeight: 700,
              textTransform: 'none',
              px: { xs: 2.5, sm: 3.5 },
              py: { xs: 1, sm: 1.25 },
              fontSize: { xs: '0.9rem', sm: '1rem' },
              '&:hover': {
                background: 'linear-gradient(45deg, #FF8C35 30%, #FFC74D 70%)',
                boxShadow: '0 6px 20px rgba(255, 107, 53, 0.45)'
              }
            }}
          >
            Explore Gifting Options
          </Button>
        </Box>
      </Container>

      <Dialog
        open={comingSoonDialog.open}
        onClose={handleComingSoonClose}
        PaperProps={{
          sx: {
            background: 'rgba(26, 26, 26, 0.98)',
            border: '2px solid rgba(255, 107, 53, 0.45)',
            borderRadius: 3,
            px: { xs: 1, sm: 2 },
            maxWidth: 420
          }
        }}
      >
        <DialogTitle
          sx={{
            color: '#FFB74D',
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            pb: 1
          }}
        >
          <ConstructionIcon sx={{ color: '#FF6B35' }} />
          {comingSoonDialog.title}
        </DialogTitle>
        <DialogContent>
          <Typography
            sx={{
              color: 'rgba(255, 255, 255, 0.85)',
              fontSize: { xs: '0.95rem', sm: '1.05rem' },
              lineHeight: 1.6
            }}
          >
            {comingSoonDialog.message}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleComingSoonClose}
            variant="contained"
            sx={{
              background: 'linear-gradient(45deg, #FF6B35 30%, #FFB74D 70%)',
              color: 'white',
              fontWeight: 700,
              textTransform: 'none',
              '&:hover': {
                background: 'linear-gradient(45deg, #FF8C35 30%, #FFC74D 70%)'
              }
            }}
          >
            Got it!
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default UserDashboard;
